import type { Bill, KitchenOrder, Payment, Tab, TabProduct } from '../types'

type NormalizedTabsData = {
  products?: Record<string, TabProduct>
  tabs?: Record<string, Tab>
  bills?: Record<string, Bill>
  payments?: Record<string, Payment>
  kitchenOrders?: Record<string, KitchenOrder>
}

function normalize(input: Tab | Tab[]): NormalizedTabsData {
  const entities: {
    products: Record<string, TabProduct>
    payments: Record<string, Payment>
    bills: Record<string, Bill>
    kitchenOrders: Record<string, KitchenOrder>
    tabs: Record<string, any>
  } = { products: {}, payments: {}, bills: {}, kitchenOrders: {}, tabs: {} }

  const idOf = (x: any): string | null => (x && x.id != null ? String(x.id) : null)

  const pushId = <T>(arr: T[], add: (x: T) => string | null): string[] => {
    const out: string[] = []
    for (let i = 0; i < arr.length; i++) {
      const k = add(arr[i])
      if (k) out.push(k)
    }
    return out
  }

  const add = {
    product: (p: any): string | null => {
      const k = idOf(p)
      if (!k) return null
      entities.products[k] = p
      return k
    },
    payment: (p: any): string | null => {
      const k = idOf(p)
      if (!k) return null
      entities.payments[k] = p
      return k
    },
    bill: (b: any): string | null => {
      const k = idOf(b)
      if (!k) return null
      const payments = pushId(b.payments ?? [], add.payment)
      entities.bills[k] = { ...b, payments }
      return k
    },
    kitchenOrder: (ko: any): string | null => {
      const k = idOf(ko)
      if (!k) return null
      entities.kitchenOrders[k] = ko
      return k
    }
  }

  const tabs = Array.isArray(input) ? input : [input]
  for (let i = 0; i < tabs.length; i++) {
    const t = tabs[i]
    const key = idOf(t)
    if (!key) continue

    const shared = pushId(t.shared ?? [], add.product)
    const seats = (t.seats ?? []).map(seat => pushId(seat ?? [], add.product))
    const kitchenOrders = pushId(t.kitchenOrders ?? [], add.kitchenOrder)
    const bills = pushId(t.bills ?? [], add.bill)

    const next = { ...t, shared, seats, kitchenOrders, bills }
    entities.tabs[key] = next
  }

  return entities
}

export function normalizeTab(tab: Tab): NormalizedTabsData {
  return normalize(tab)
}

export function normalizeTabs(tabs: Tab[]): NormalizedTabsData {
  return normalize(tabs)
}
